import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import {
  type CreateDefaultMedicalDataSchema,
  MedicalDataCategory,
  SupportedMedicalData,
  apiPostHfData,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useSupportedMedicalData } from "../../../catalogs/supported-medical-data";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  RowAccordion,
  useRowAccordion,
} from "../../../components/ui-kit/tables/row-accordion";
import {
  getNamesValues,
  getPrimaryUnitByName,
  getUnitsValuesByName,
  getVariableByName,
} from "../../../utils/data-utils";
import { getServerNowISO } from "../../../utils/server-time";
import { createRowId } from "../../../utils/utils";
import { GenericValueField } from "../common/generic-value-field";

export const AddVitalsDialog = observer(function AddVitalsDialog({
  app,
  variant,
  onClose,
  onSaved,
}: {
  app: AppModel;
  variant: "vitals" | "basics";
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      className="top-[5dvh] max-h-[90dvh] w-[750px] translate-y-0"
    >
      <AddVitalsBody
        app={app}
        variant={variant}
        onClose={onClose}
        onSaved={onSaved}
      />
    </Dialog>
  );
});

const AddVitalsBody = observer(function AddVitalsBody({
  app,
  variant,
  onClose,
  onSaved,
}: {
  app: AppModel;
  variant: "vitals" | "basics";
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const patient = app.requirePatient();
  const api = app.seerlinqApi;
  const supportedMedData = useSupportedMedicalData();

  const supportedVariables = useMemo(
    () =>
      supportedMedData.filter((data) =>
        variant === "vitals"
          ? data.data_category === MedicalDataCategory.vitals
          : data.data_category === MedicalDataCategory.basic,
      ),
    [supportedMedData, variant],
  );

  const defaultDateTime = useMemo(
    () =>
      variant === "vitals"
        ? getServerNowISO()
        : (patient.data?.created_at ?? ""),
    [variant, patient.data?.created_at],
  );

  const createRow = useCallback(
    (v?: SupportedMedicalData) =>
      observable({
        __rowId: createRowId(),
        measurement_datetime: defaultDateTime,
        measurement_type:
          v?.data_name ?? supportedVariables[0]?.data_name ?? "",
        measurement_value: null as number | null,
        measurement_unit:
          v?.primary_unit ?? supportedVariables[0]?.primary_unit ?? "",
        comment: "",
      }),
    [defaultDateTime, supportedVariables],
  );

  // supportedVariables is derived synchronously, so seed one row per variable up
  // front (computed once) instead of populating via an effect after first render.
  const [initialRows] = useState(() =>
    supportedVariables.map((v) => createRow(v)),
  );
  const accordion = useRowAccordion(createRow, initialRows);

  const hasValidRows =
    accordion.rows.length > 0 &&
    accordion.rows.every((r) => isVitalsRowComplete(r));

  async function save() {
    const items = accordion.rows.map((row) =>
      toVitalsPayload(row, patient.patientId),
    );

    try {
      const response = await apiPostHfData({
        body: {
          number_of_datapoints: items.length,
          medical_data: items,
        },
      });

      if (response.data != null) {
        api.clearResponseCache();
        onSaved();
        onClose();
      }
    } catch (error) {
      window.handleError(error);
    }
  }

  return (
    <>
      <h3 className="mb-6 text-base font-bold">
        {variant === "vitals" ? t`Add vitals` : t`Add basic medical data`}
      </h3>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <div className="min-h-0 overflow-y-auto overflow-x-hidden py-1 px-1">
          <RowAccordion
            {...accordion}
            summaryLabel={(row) => (
              <>
                {row.measurement_type}
                {row.measurement_value != null && (
                  <span className="text-muted-foreground">
                    — {row.measurement_value} {row.measurement_unit}
                  </span>
                )}
              </>
            )}
            renderRow={(row) => (
              <RowForm
                row={row}
                variableDef={getVariableByName(
                  supportedVariables,
                  row.measurement_type,
                )}
                supportedVariables={supportedVariables}
              />
            )}
          />
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit" disabled={!hasValidRows}>
            <Trans>Add</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </>
  );
});

const RowForm = observer(function RowForm({
  row,
  variableDef,
  supportedVariables,
}: {
  row: VitalsRow;
  variableDef: SupportedMedicalData | undefined;
  supportedVariables: SupportedMedicalData[];
}) {
  const { t } = useLingui();
  // Variables can support more than one unit (e.g. weight in kg/lb). Offer the
  // choice when there's one to make; otherwise the unit is fixed (primary).
  const unitOptions = getUnitsValuesByName(
    supportedVariables,
    row.measurement_type,
  );
  return (
    <FormTable dataAdd className="max-w-full">
      <FormRow
        label={t`Measured at`}
        data={
          <DateInput
            model={row}
            attr="measurement_datetime"
            type="datetime-local"
          />
        }
        mandatory
      />
      <FormRow
        label={t`Type`}
        data={
          <Select
            model={row}
            attr="measurement_type"
            values={getNamesValues(supportedVariables)}
            onChange={(newValue) => {
              row.measurement_unit =
                getPrimaryUnitByName(supportedVariables, newValue) ?? null;
            }}
          />
        }
        mandatory
      />
      <FormRow
        label={t`Value`}
        data={
          variableDef ? (
            <GenericValueField
              model={row}
              attr="measurement_value"
              variableDef={variableDef}
            />
          ) : null
        }
        mandatory
      />
      <FormRow
        label={t`Unit`}
        data={
          unitOptions.length > 1 ? (
            <Select model={row} attr="measurement_unit" values={unitOptions} />
          ) : (
            <span>{row.measurement_unit}</span>
          )
        }
      />
      <FormRow
        label={t`Comment`}
        data={
          <TextInput
            model={row}
            attr="comment"
            multiline="autosize"
            width={330}
          />
        }
      />
    </FormTable>
  );
});

type VitalsRow = {
  __rowId: string;
  measurement_datetime: string;
  measurement_type: string;
  measurement_value: number | null;
  measurement_unit: string | null;
  comment: string;
};

function toVitalsPayload(
  row: VitalsRow,
  patientId: number,
): CreateDefaultMedicalDataSchema {
  if (row.measurement_value == null) {
    throw new Error("measurement_value is required");
  }
  return {
    measurement_datetime: row.measurement_datetime,
    measurement_type: row.measurement_type,
    measurement_value: row.measurement_value,
    measurement_unit: row.measurement_unit,
    comment: row.comment,
    patient_id: patientId,
  };
}

function isVitalsRowComplete(row: VitalsRow): boolean {
  return (
    !!row.measurement_datetime &&
    !!row.measurement_type &&
    row.measurement_value != null
  );
}
